import React from "react";

export type NavSection = { cat: string; items: { slug: string; label: string; needsSetup?: boolean }[] };

type SelectionPanelProps = {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    headerActions?: React.ReactNode;
    banner?: React.ReactNode;
    nav: NavSection[];
    activeSlug?: string;
    onSelectSection: (slug: string) => void;
    children: React.ReactNode;     // the active section's content
    footer?: React.ReactNode;      // Save/Cancel
    emptyNavNote?: string;
};

// Presentational only: it knows nothing about integrations. The nav switches the
// content instead of scrolling to it, so exactly one section renders at a time.
const SelectionPanel = ({
    title, subtitle, headerActions, banner, nav, activeSlug, onSelectSection, children, footer, emptyNavNote,
}: SelectionPanelProps) => (
    <div className="border border-gray-lines rounded-lg bg-white">
        <div className="flex flex-wrap items-center gap-3 bg-light-gray rounded-t-lg px-5 py-3 border-b border-gray-lines">
            <span className="text-lg font-bold text-off-black">{title}</span>
            {subtitle && <span className="text-sm text-secondary-text">{subtitle}</span>}
            {headerActions && <span className="ml-auto flex items-center gap-3">{headerActions}</span>}
        </div>

        <div className="flex gap-6 px-5 py-5">
            {/* Always visible, at every width: the nav is the only way to reach the
                other sections, so hiding it on narrow hosts (a modal, a phone)
                would strand them. Hosts size themselves to fit this column. */}
            <nav className="w-40 md:w-48 flex-none pt-1">
                {nav.map((section) => (
                    <div key={section.cat} className="mb-4">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-medium-gray mb-1.5">{section.cat}</div>
                        {section.items.length === 0 && emptyNavNote && (
                            <div className="text-sm text-medium-gray italic pl-2 border-l-2 border-gray-lines py-1">{emptyNavNote}</div>
                        )}
                        {section.items.map((item) => (
                            <button key={item.slug} type="button"
                                title={item.needsSetup ? `${item.label} — needs setup` : item.label}
                                className={`flex items-center gap-1.5 w-full text-left text-sm py-1 pl-2 border-l-2 ${
                                    item.slug === activeSlug
                                        ? "text-connection-green border-connection-green font-semibold"
                                        : "text-secondary-text border-gray-lines hover:text-connection-green hover:border-connection-green"}`}
                                onClick={() => onSelectSection(item.slug)}>
                                <span className="truncate">{item.label}</span>
                                {item.needsSetup && (
                                    <span className="flex-none w-3.5 h-3.5 rounded-full bg-orange-50 text-orange border border-orange-200 text-[9px] font-bold flex items-center justify-center">
                                        !
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="flex-1 min-w-0">
                {banner}
                {children}
            </div>
        </div>

        {footer && <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-lines">{footer}</div>}
    </div>
);

export default SelectionPanel;
